/**
 * Colour rules for a lab's branding, the same arithmetic the server applies
 * (`smart_reports_models.lab_branding`, contract 1.5 and 4.3).
 *
 * The editor uses these to show contrast feedback and the ink a lab would get before it
 * saves. **The server decides**: it refuses an ink that fails and derives the ink itself.
 * These are ported so the editor never tells a lab a colour passes that the save then
 * refuses. `color.test.ts` pins both against the server's own case file.
 */

/** The two page backgrounds report-summary draws on. Ink must pass on both. */
export const CONTRAST_BACKGROUNDS = ["#ffffff", "#f6f8fb"] as const;
export const MIN_CONTRAST = 4.5;

const HEX_COLOR = /^#[0-9a-f]{6}$/;

/** `#rrggbb` in lowercase hex, the only form the server accepts. */
export function isHexColor(value: string): boolean {
  return HEX_COLOR.test(value);
}

function rgb(color: string): [number, number, number] {
  if (!isHexColor(color)) {
    throw new Error("a colour is #rrggbb in lowercase hex");
  }
  return [
    parseInt(color.slice(1, 3), 16),
    parseInt(color.slice(3, 5), 16),
    parseInt(color.slice(5, 7), 16),
  ];
}

function srgbToLinear(c: number): number {
  return c <= 0.04045 ? c / 12.92 : ((c + 0.055) / 1.055) ** 2.4;
}

function linearToSrgb(c: number): number {
  return c <= 0.0031308 ? 12.92 * c : 1.055 * c ** (1 / 2.4) - 0.055;
}

function linearRgb(color: string): [number, number, number] {
  const [r, g, b] = rgb(color);
  return [srgbToLinear(r / 255), srgbToLinear(g / 255), srgbToLinear(b / 255)];
}

/** WCAG 2 relative luminance of `#rrggbb`. */
export function relativeLuminance(color: string): number {
  const [r, g, b] = linearRgb(color);
  return 0.2126 * r + 0.7152 * g + 0.0722 * b;
}

/** WCAG 2 contrast ratio between two `#rrggbb` colours, 1 to 21, unrounded. */
export function contrastRatio(a: string, b: string): number {
  const la = relativeLuminance(a);
  const lb = relativeLuminance(b);
  return (Math.max(la, lb) + 0.05) / (Math.min(la, lb) + 0.05);
}

/** True when `ink` reaches 4.5:1 on every page background. */
export function inkPassesContrast(ink: string): boolean {
  return CONTRAST_BACKGROUNDS.every((bg) => contrastRatio(ink, bg) >= MIN_CONTRAST);
}

function toOklch(color: string): [number, number, number] {
  const [r, g, b] = linearRgb(color);
  const lm = 0.4122214708 * r + 0.5363325363 * g + 0.0514459929 * b;
  const mm = 0.2119034982 * r + 0.6806995451 * g + 0.1073969566 * b;
  const sm = 0.0883024619 * r + 0.2817188376 * g + 0.6299787005 * b;
  const [l_, m_, s_] = [Math.cbrt(lm), Math.cbrt(mm), Math.cbrt(sm)];
  const lightness = 0.2104542553 * l_ + 0.793617785 * m_ - 0.0040720468 * s_;
  const a = 1.9779984951 * l_ - 2.428592205 * m_ + 0.4505937099 * s_;
  const bb = 0.0259040371 * l_ + 0.7827717662 * m_ - 0.808675766 * s_;
  return [lightness, Math.hypot(a, bb), Math.atan2(bb, a)];
}

/** Python's `round`, half to even, so a channel lands on the same integer as the server's. */
export function roundHalfEven(x: number): number {
  const floor = Math.floor(x);
  const diff = x - floor;
  if (diff > 0.5) return floor + 1;
  if (diff < 0.5) return floor;
  return floor % 2 === 0 ? floor : floor + 1;
}

function fromOklch(lightness: number, chroma: number, hue: number): string {
  const a = chroma * Math.cos(hue);
  const b = chroma * Math.sin(hue);
  const l_ = lightness + 0.3963377774 * a + 0.2158037573 * b;
  const m_ = lightness - 0.1055613458 * a - 0.0638541728 * b;
  const s_ = lightness - 0.0894841775 * a - 1.291485548 * b;
  const [lm, mm, sm] = [l_ ** 3, m_ ** 3, s_ ** 3];
  const linear = [
    4.0767416621 * lm - 3.3077115913 * mm + 0.2309699292 * sm,
    -1.2684380046 * lm + 2.6097574011 * mm - 0.3413193965 * sm,
    -0.0041960863 * lm - 0.7034186147 * mm + 1.707614701 * sm,
  ];
  const clamp = (c: number): number => Math.min(1, Math.max(0, c));
  return (
    "#" +
    linear
      .map((c) => roundHalfEven(clamp(linearToSrgb(clamp(c))) * 255))
      .map((c) => c.toString(16).padStart(2, "0"))
      .join("")
  );
}

/**
 * The ink a lab gets when it sets a primary and no ink (contract 4.3 step 5): the primary
 * when it already passes, else its OKLCH lightness lowered in steps of 0.01, chroma and hue
 * kept, until it passes on both backgrounds. Black at the end of the walk always passes.
 */
export function deriveInk(primary: string): string {
  if (inkPassesContrast(primary)) {
    return primary;
  }
  const [lightness, chroma, hue] = toOklch(primary);
  for (let step = 1; lightness - step * 0.01 > 0; step += 1) {
    const candidate = fromOklch(lightness - step * 0.01, chroma, hue);
    if (inkPassesContrast(candidate)) {
      return candidate;
    }
  }
  return "#000000";
}

/** Mix `color` toward `toward` by `amount` (0 keeps it, 1 is `toward`), as `#rrggbb`. */
export function mix(color: string, toward: string, amount: number): string {
  const [r1, g1, b1] = rgb(color);
  const [r2, g2, b2] = rgb(toward);
  const channel = (a: number, b: number): string =>
    Math.round(a + (b - a) * amount)
      .toString(16)
      .padStart(2, "0");
  return `#${channel(r1, r2)}${channel(g1, g2)}${channel(b1, b2)}`;
}
